/**
 * Authentication Service for AWS Cognito
 * 
 * Handles all authentication operations:
 * - Login, Signup, Logout
 * - Token management (idToken, accessToken, refreshToken)
 * - Token refresh
 */

import awsConfig from '../config/aws-config';

class AuthService {
  constructor() {
    this.tokenKey = 'idToken';
    this.accessTokenKey = 'accessToken';
    this.refreshTokenKey = 'refreshToken';
    this.userKey = 'user';
    this.expiresAtKey = 'expiresAt';
  }

  /**
   * User Signup
   */
  async signup(userData) {
    const response = await fetch(`${awsConfig.apiGateway.baseURL}${awsConfig.apiGateway.endpoints.signup}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Signup failed');
    }

    return data;
  }

  /**
   * User Login
   */
  async login(email, password) {
    const response = await fetch(`${awsConfig.apiGateway.baseURL}${awsConfig.apiGateway.endpoints.login}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Create axios-like error structure for consistency
      const error = new Error(data.error || data.message || 'Login failed');
      error.response = { data };
      error.status = response.status;
      throw error;
    }

    // Store tokens
    this.setTokens(data.idToken, data.accessToken, data.refreshToken, data.expiresIn);
    
    // Store user info
    if (data.user) {
      localStorage.setItem(this.userKey, JSON.stringify(data.user));
      // Legacy support
      localStorage.setItem('usermame', data.user.email);
    }

    return data;
  }

  /**
   * Login with phone number + password
   */
  async loginWithPhone(phone, password) {
    const response = await fetch(`${awsConfig.apiGateway.baseURL}${awsConfig.apiGateway.endpoints.login}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || data.message || 'Login failed');
      error.response = { data };
      error.status = response.status;
      throw error;
    }

    this.setTokens(data.idToken, data.accessToken, data.refreshToken, data.expiresIn);
    if (data.user) {
      localStorage.setItem(this.userKey, JSON.stringify(data.user));
      localStorage.setItem('usermame', data.user.email);
    }

    return data;
  }

  /**
   * User Logout
   */
  logout() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.expiresAtKey);
    // Legacy keys
    localStorage.removeItem('usermame');
    localStorage.removeItem('token');
    localStorage.removeItem('planName');
    localStorage.removeItem('chatUser');
  }

  /**
   * Store tokens in localStorage
   */
  setTokens(idToken, accessToken, refreshToken, expiresIn) {
    localStorage.setItem(this.tokenKey, idToken);
    localStorage.setItem(this.accessTokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
    
    // Calculate expiration time (expiresIn is in seconds)
    const expiresAt = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.expiresAtKey, expiresAt.toString());

    // Legacy support - store idToken as 'token' for existing code
    localStorage.setItem('token', idToken);
  }

  /**
   * Get ID Token (used for authenticated requests)
   */
  getIdToken() {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Get Access Token
   */
  getAccessToken() {
    return localStorage.getItem(this.accessTokenKey);
  }

  /**
   * Get Refresh Token
   */
  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Get current user from localStorage
   */
  getCurrentUser() {
    const userStr = localStorage.getItem(this.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    const token = this.getIdToken();
    if (!token) return false;

    // Check if token is expired
    const expiresAt = localStorage.getItem(this.expiresAtKey);
    if (expiresAt && Date.now() >= parseInt(expiresAt)) {
      // Token expired, try to refresh
      this.refreshTokens().catch(() => {
        this.logout();
      });
      return false;
    }

    return true;
  }

  /**
   * Refresh tokens
   */
  async refreshTokens() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${awsConfig.apiGateway.baseURL}${awsConfig.apiGateway.endpoints.refresh}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Token refresh failed');
    }

    // Update tokens
    this.setTokens(data.idToken, data.accessToken, refreshToken, data.expiresIn);

    return data;
  }

  /**
   * Confirm Email Signup
   */
  async confirmSignup(email, code) {
    const response = await fetch(`${awsConfig.apiGateway.baseURL}${awsConfig.apiGateway.endpoints.confirmSignup}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Email verification failed');
    }

    return data;
  }

  /**
   * Resend Verification Code
   */
  async resendCode(email) {
    const response = await fetch(`${awsConfig.apiGateway.baseURL}${awsConfig.apiGateway.endpoints.resendCode}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Failed to resend verification code');
    }

    return data;
  }

  /**
   * Get Authorization header for API requests
   */
  getAuthHeader() {
    const token = this.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Check if current user is in Cognito 'Admins' group by decoding idToken
   */
  isAdmin() {
    try {
      const token = this.getIdToken();
      if (!token) return false;
      const parts = token.split('.');
      if (parts.length < 2) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const groups = payload['cognito:groups'] || payload['groups'] || [];
      return Array.isArray(groups) && groups.includes('Admins');
    } catch (e) {
      return false;
    }
  }
}

export default new AuthService();
